import { makeAutoObservable, runInAction } from 'mobx';
import {
  LanguageType,
  OrganizationApiResponse,
  OrganizationApiResponseWithPopulatedPlan,
  OrganizationFormFields,
} from 'src/types/organization';
import createStoreContext from '../createStoreContext';
import {
  changeActiveOrganization as changeActiveOrganizationApi,
  checkDnsVerification,
  createOrganizationDetails,
  getCurrentOrganizationDetails,
  getSupportedTimezones,
  inviteUsersToOrgViaEmail as inviteUsersToOrgViaEmailApi,
  joinOrganizationWithInviteToken as joinOrganizationWithInviteTokenApi,
  saveOnboardingDetails as saveOnboardingDetailsApi,
  updateOrganizationDetails,
  getOrganizationsForDomainDirectory,
  lookupOrganizationsForDomainDirectoryByEmail,
  createOrganizationJoinRequest,
  listOrganizationJoinRequests,
  handleOrganizationJoinRequestDecision as handleOrganizationJoinRequestDecisionApi,
} from 'src/services/organization';
import { getLanguages } from 'src/services/languages';
import {
  OnboardingDetailsPayload,
  Option,
} from 'src/types/registrationFormTypes';
import { getAllCountries } from 'countries-and-timezones';
import {
  OrganizationDomainSuggestion,
  OrganizationJoinRequest,
  OrganizationJoinRequestDecisionPayload,
} from 'src/types/organization';

const countries = getAllCountries();

export const [OrganizationProvider, useOrganizationStore] = createStoreContext(
  class OrganizationStore {
    private _activeOrganizationDetails: OrganizationApiResponseWithPopulatedPlan | null =
      null;
    supportedLanguages: Option[] = [];
    supportedTimezones: Option[] = [];
    isDnsVerificationComplete = false;
    domainDirectorySuggestions: OrganizationDomainSuggestion[] = [];
    isDomainDirectoryLoading = false;
    organizationJoinRequests: OrganizationJoinRequest[] = [];
    organizationJoinRequestsTotalCount = 0;
    isOrganizationJoinRequestsLoading = false;

    constructor() {
      makeAutoObservable(this);
    }

    /**
     * Gives back the active organization set by the user
     */
    get organizationDetails() {
      return this._activeOrganizationDetails;
    }

    set organizationDetails(value) {
      this._activeOrganizationDetails = value;
    }

    resetOrganizationState = () => {
      runInAction(() => {
        this._activeOrganizationDetails = null;
        this.isDnsVerificationComplete = false;
        this.domainDirectorySuggestions = [];
        this.organizationJoinRequests = [];
        this.organizationJoinRequestsTotalCount = 0;
      });
    };

    patchOrganizationDetails = (
      patch:
        | Partial<OrganizationApiResponseWithPopulatedPlan>
        | Partial<OrganizationApiResponse>,
    ) => {
      if (!this._activeOrganizationDetails) return;

      const nextPatch = { ...patch } as Partial<
        OrganizationApiResponseWithPopulatedPlan & OrganizationApiResponse
      >;

      if (typeof nextPatch.planId === 'string') {
        delete nextPatch.planId;
      }

      this._activeOrganizationDetails = {
        ...this._activeOrganizationDetails,
        ...nextPatch,
      };
    };

    get supportedCountries() {
      return Object.values(countries).map((country) => ({
        name: country.name,
        id: country.id,
      }));
    }

    fetchCurrentOrganization = async () => {
      const response = await getCurrentOrganizationDetails();
      const organizationDetails = response.content;

      if (response.success && organizationDetails) {
        runInAction(() => {
          this._activeOrganizationDetails = organizationDetails;
        });
      }

      return response;
    };

    fetchSupportedLanguages = async () => {
      const { success: getLanguagesSuccess, content: getLanguagesData } =
        await getLanguages();

      if (!getLanguagesSuccess || !getLanguagesData) {
        return null;
      }

      const mappedLanguages = getLanguagesData.map(
        ({ name, languageCode }: LanguageType) => ({
          id: languageCode,
          name: name,
        }),
      );

      runInAction(() => {
        this.supportedLanguages = mappedLanguages;
      });

      return mappedLanguages;
    };

    fetchSupportedTimezones = async () => {
      const { success, content } = await getSupportedTimezones();

      if (!success || !content) {
        return null;
      }

      const mappedTimezones = content.map((timezone) => ({
        id: timezone,
        name: timezone,
      }));

      runInAction(() => {
        this.supportedTimezones = mappedTimezones;
      });

      return mappedTimezones;
    };

    updateOrganization = async (data: OrganizationFormFields) => {
      const payload = {
        ...data,
        industrySize: data.companySize,
      };

      const response = await updateOrganizationDetails(payload);
      const organizationDetails = response.content;

      if (response.success && organizationDetails) {
        runInAction(() => {
          this._activeOrganizationDetails = organizationDetails;
        });
      }

      return response;
    };

    createNewOrganization = async (newOrgPayload: OrganizationFormFields) => {
      const payload = {
        ...newOrgPayload,
        industrySize: newOrgPayload.companySize,
      };

      const response = await createOrganizationDetails(payload);
      const organizationDetails = response.content;

      if (response.success && organizationDetails) {
        runInAction(() => {
          this._activeOrganizationDetails = organizationDetails;
        });
      }

      return response;
    };

    saveOnboardingDetails = async (payload: OnboardingDetailsPayload) => {
      const response = await saveOnboardingDetailsApi(payload);

      return response;
    };

    getDnsVerification = async () => {
      const response = await checkDnsVerification();

      if (response.success && response.content) {
        runInAction(() => {
          this.isDnsVerificationComplete = response.content!.isDnsVerified;
        });
      }

      return response;
    };

    changeActiveOrganization = async (orgId: string) => {
      const response = await changeActiveOrganizationApi(orgId);

      if (response.success) {
        await this.fetchCurrentOrganization();
      }

      return response;
    };

    joinOrganizationWithInviteToken = async (inviteToken: string) => {
      const response = await joinOrganizationWithInviteTokenApi(inviteToken);

      if (
        response.success &&
        response.content &&
        response.content.organizationDetails &&
        response.content.organizationDetails._id
      ) {
        runInAction(() => {
          this._activeOrganizationDetails =
            response.content!.organizationDetails;
        });
      }

      return response;
    };

    inviteUsersToOrgViaEmail = async (emails: string[]) => {
      const response = await inviteUsersToOrgViaEmailApi(emails);

      return response;
    };

    fetchOrganizationsForDomainDirectory = async () => {
      this.isDomainDirectoryLoading = true;

      try {
        let response = await lookupOrganizationsForDomainDirectoryByEmail();

        if (!response.success) {
          response = await getOrganizationsForDomainDirectory();
        }

        if (response.success && response.content) {
          runInAction(() => {
            this.domainDirectorySuggestions = response.content ?? [];
          });
        } else {
          runInAction(() => {
            this.domainDirectorySuggestions = [];
          });
        }

        return response;
      } finally {
        runInAction(() => {
          this.isDomainDirectoryLoading = false;
        });
      }
    };

    submitOrganizationJoinRequest = async (
      organizationId: string,
      requestMessage?: string,
    ) => {
      const response = await createOrganizationJoinRequest(
        organizationId,
        requestMessage,
      );

      return response;
    };

    fetchOrganizationJoinRequests = async (
      status?: string,
      page?: number,
      limit?: number,
    ) => {
      this.isOrganizationJoinRequestsLoading = true;

      try {
        const response = await listOrganizationJoinRequests(
          status,
          page,
          limit,
        );

        if (response.success && response.content) {
          const requests = Array.isArray(response.content)
            ? response.content
            : (response.content.data ?? []);
          const totalCount = Array.isArray(response.content)
            ? response.content.length
            : (response.content.totalCount ?? 0);

          runInAction(() => {
            this.organizationJoinRequests = requests;
            this.organizationJoinRequestsTotalCount = totalCount;
          });
        } else {
          runInAction(() => {
            this.organizationJoinRequests = [];
            this.organizationJoinRequestsTotalCount = 0;
          });
        }

        return response;
      } finally {
        runInAction(() => {
          this.isOrganizationJoinRequestsLoading = false;
        });
      }
    };

    decideOrganizationJoinRequest = async (
      requestId: string,
      payload: OrganizationJoinRequestDecisionPayload,
    ) => {
      const response = await handleOrganizationJoinRequestDecisionApi(
        requestId,
        payload,
      );

      if (response.success && response.content) {
        runInAction(() => {
          this.organizationJoinRequests = this.organizationJoinRequests.map(
            (request) => {
              const requestMatchId = request._id ?? request.requestId;

              return requestMatchId === requestId
                ? { ...request, ...response.content }
                : request;
            },
          );
        });
      }

      return response;
    };
  },
);

export type OrganizationStore = ReturnType<typeof useOrganizationStore>;
